/**
 * The shared pickup create/edit form (workstream C) — used by both the New-pickup
 * dialog (Pickups page) and the Edit dialog (pickup detail). Owns its own state,
 * seeded by `initial`, and emits a normalized payload on submit: the orders the
 * trip covers (project multi-select), the ordered stops (reusing
 * PickupLocationsEditor), an optional window, a name, and notes.
 */
import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import type { PickupLocation } from '@vari-crew/schemas';
import { useProjects } from '@/api/projects';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { PickupLocationsEditor } from '@/components/projects/PickupLocationsEditor';
import { cn } from '@/lib/cn';

export interface PickupFormValue {
  title: string | null;
  project_ids: string[];
  stops: PickupLocation[];
  scheduled_start: string | null;
  scheduled_end: string | null;
  notes: string | null;
}

/** ISO instant → a `datetime-local` value (local wall time), or ''. */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
/** `datetime-local` value → ISO instant, or null. */
function localToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function PickupForm({
  initial,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  initial?: Partial<PickupFormValue>;
  submitLabel: string;
  pending: boolean;
  onSubmit: (value: PickupFormValue) => void;
  onCancel: () => void;
}) {
  const projectsQ = useProjects({ limit: 200, offset: 0 });
  const allProjects = projectsQ.data?.data ?? [];

  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set(initial?.project_ids ?? []));
  const [title, setTitle] = useState(initial?.title ?? '');
  const [start, setStart] = useState(isoToLocalInput(initial?.scheduled_start ?? null));
  const [end, setEnd] = useState(isoToLocalInput(initial?.scheduled_end ?? null));
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [stops, setStops] = useState<PickupLocation[]>(initial?.stops ?? []);

  const filtered = useMemo(() => {
    // Selected projects always show (so an edit never hides a linked order that
    // has since gone complete); otherwise only live projects are selectable.
    const selectable = allProjects.filter(
      (p) => selected.has(p.id) || (p.status !== 'complete' && p.status !== 'cancelled'),
    );
    const q = filter.trim().toLowerCase();
    if (!q) return selectable;
    return selectable.filter(
      (p) => p.title.toLowerCase().includes(q) || (p.client_name ?? '').toLowerCase().includes(q),
    );
  }, [allProjects, filter, selected]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const windowInverted = !!start && !!end && new Date(start) >= new Date(end);
  const canSave = selected.size > 0 && !windowInverted && !pending;

  function submit() {
    onSubmit({
      title: title.trim() || null,
      project_ids: [...selected],
      stops,
      scheduled_start: localToIso(start),
      scheduled_end: localToIso(end),
      notes: notes.trim() || null,
    });
  }

  return (
    <div className="p-4 space-y-4">
      <div>
        <Label htmlFor="pickup-title">Name (optional)</Label>
        <Input
          id="pickup-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Friday warehouse run — otherwise derived from the stops"
        />
      </div>

      <div>
        <Label>Orders this trip covers</Label>
        <p className="text-[11px] text-ink-faint mb-1.5">
          The crew check off each project’s packing list as they load it.
        </p>
        <div className="relative mb-1.5">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-ink-faint" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter projects…"
            className="pl-7 h-8"
          />
        </div>
        {projectsQ.isLoading ? (
          <div className="text-xs text-ink-faint py-3 text-center">Loading projects…</div>
        ) : filtered.length === 0 ? (
          <div className="text-xs text-ink-faint py-3 text-center">No matching projects.</div>
        ) : (
          <ul className="space-y-1 max-h-44 overflow-auto border border-line rounded-md p-1">
            {filtered.map((p) => (
              <li key={p.id}>
                <label
                  className={cn(
                    'flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer text-sm',
                    selected.has(p.id) ? 'bg-accent/5' : 'hover:bg-fill-soft',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggle(p.id)}
                    className="shrink-0"
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {p.title}
                    {p.client_name && <span className="text-ink-faint"> · {p.client_name}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <div className="text-[11px] text-ink-faint mt-1">{selected.size} selected</div>
      </div>

      <div>
        <Label>Stops (in order)</Label>
        <PickupLocationsEditor value={stops} onChange={setStops} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="pickup-start">Starts</Label>
          <Input id="pickup-start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="pickup-end">Ends</Label>
          <Input id="pickup-end" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>
      {windowInverted && <p className="text-[11px] text-status-blocked-fg">End must be after the start.</p>}

      <div>
        <Label htmlFor="pickup-notes">Notes (optional)</Label>
        <Textarea
          id="pickup-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything the crew should know for this run."
        />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={!canSave}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
